import { useState, useContext, useCallback } from 'react';
import { GlobalContext } from '../contexts/global-context';
import { AuthContext } from '../contexts/auth-context';
import useApi from '../services/api';

export const useSocialAuth = () => {
    const { setShowAuthModal, setAuthModalType, setSsoUserData } = useContext(GlobalContext);
    const { storeUser, setEmail, setSessionToken } = useContext(AuthContext);
    const { loginWithProvider } = useApi();
    const [oauthLoading, setOauthLoading] = useState(false);
    const [generalError, setGeneralError] = useState({
        isErrorHidden: true,
        errorMessage: ""
    });

    const handleAuthResponse = useCallback(async (userData, provider) => {
        // Check for errors in new response format
        if (userData.meta?.error === 'email_required') {
            // User needs to provide email - don't store tokens yet
            setSsoUserData({
                user: userData.data?.user || userData.user,
                provider: provider,
                sessionToken: userData.meta?.session_token
            });
            setAuthModalType("sso-email");
        } else if (userData.meta?.error === 'email_not_verified') {
            // User has email but needs to verify it
            setEmail(userData.data?.user?.email || userData.user?.email);
            setSessionToken(userData.meta?.session_token);
            setAuthModalType("emailed");
        } else {
            // Normal login with tokens
            storeUser(userData);
            setShowAuthModal(false);
        }
        setOauthLoading(false);
    }, [setSsoUserData, setAuthModalType, setEmail, setSessionToken, storeUser, setShowAuthModal]);

    const handleGoogleAuth = useCallback(async () => {
        const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
        
        if (!clientId) {
            setGeneralError({
                isErrorHidden: false,
                errorMessage: 'Google Client ID not configured'
            });
            return;
        }
        
        setOauthLoading(true);
        
        try {
            // Load Google Identity Services if not already loaded
            if (!window.google?.accounts?.id) {
                const script = document.createElement('script');
                script.src = 'https://accounts.google.com/gsi/client';
                script.async = true;
                script.defer = true;
                document.body.appendChild(script);
                
                // Wait for the script to load
                await new Promise((resolve) => {
                    script.onload = resolve;
                });
                
                // Wait a bit for the library to initialize
                await new Promise(resolve => setTimeout(resolve, 100));
            }
            
            // Simple initialize and prompt approach
            window.google.accounts.id.initialize({
                client_id: clientId,
                callback: async (response) => {
                    try {
                        
                        if (!response.credential) {
                            throw new Error('No credential received');
                        }
                        
                        // Send ID token to backend
                        const userData = await loginWithProvider('google', response.credential, true);
                        await handleAuthResponse(userData, 'google');
                    } catch (err) {
                        setOauthLoading(false);
                        setGeneralError({
                            isErrorHidden: false,
                            errorMessage: err.message || 'Authentication failed'
                        });
                    }
                },
                auto_select: false,
                cancel_on_tap_outside: true
            });
            
            // Try to prompt
            window.google.accounts.id.prompt((notification) => {
                if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
                    setOauthLoading(false);
                }
            });
            
        } catch (error) {
            setOauthLoading(false);
            setGeneralError({
                isErrorHidden: false,
                errorMessage: 'Failed to initialize Google Sign-In'
            });
        }
    }, [loginWithProvider, handleAuthResponse]);

    const handleFacebookAuth = useCallback(() => {
        const appId = process.env.REACT_APP_FACEBOOK_APP_ID;
        
        if (!appId) {
            setGeneralError({
                isErrorHidden: false,
                errorMessage: 'Facebook App ID not configured'
            });
            return;
        }
        
        setOauthLoading(true);
        
        // Initialize Facebook SDK using official pattern
        const initializeFacebookSDK = () => {
            window.fbAsyncInit = function() {
                window.FB.init({
                    appId: appId,
                    cookie: true,
                    xfbml: true,
                    version: 'v19.0'
                });
                
                // Now perform the login
                performFacebookLogin();
            };

            // Load SDK if not already loaded
            if (!document.getElementById('facebook-jssdk')) {
                const fjs = document.getElementsByTagName('script')[0];
                const js = document.createElement('script');
                js.id = 'facebook-jssdk';
                js.src = 'https://connect.facebook.net/en_US/sdk.js';
                fjs.parentNode.insertBefore(js, fjs);
            } else if (window.FB) {
                // SDK already loaded
                performFacebookLogin();
            }
        };
        
        const performFacebookLogin = () => {
            // Check if user is already connected
            window.FB.getLoginStatus(function(response) {
                if (response.status === 'connected') {
                    // Already connected, use existing token
                    handleFacebookResponse(response);
                } else {
                    // Need to login
                    window.FB.login(function(loginResponse) {
                        handleFacebookResponse(loginResponse);
                    }, { scope: 'email' });
                }
            });
        };
        
        const handleFacebookResponse = (response) => {
            if (response.status !== 'connected') {
                setOauthLoading(false);
                if (response.status === 'not_authorized') {
                    setGeneralError({
                        isErrorHidden: false,
                        errorMessage: 'Please authorize the app to continue'
                    });
                } else {
                    setGeneralError({
                        isErrorHidden: false,
                        errorMessage: 'Facebook login was cancelled'
                    });
                }
                return;
            }
            
            // Get the access token
            const accessToken = response.authResponse.accessToken;

            // Send access token to backend (async operation inside non-async callback)
            loginWithProvider('facebook', accessToken, true)
                .then((userData) => {
                    return handleAuthResponse(userData, 'facebook');
                })
                .catch((err) => {
                    setOauthLoading(false);
                    
                    // Extract detailed error message
                    let errorMessage = 'Facebook authentication failed';
                    if (err.response?.data?.errors) {
                        errorMessage = err.response.data.errors[0]?.message || errorMessage;
                    } else if (err.response?.data?.detail) {
                        errorMessage = err.response.data.detail;
                    } else if (err.response?.data?.error) {
                        errorMessage = err.response.data.error;
                    }
                    
                    setGeneralError({
                        isErrorHidden: false,
                        errorMessage: errorMessage
                    });
                });
        };
        
        // Start the initialization
        initializeFacebookSDK();
    }, [loginWithProvider, handleAuthResponse]);

    return {
        handleGoogleAuth,
        handleFacebookAuth,
        oauthLoading,
        generalError,
        setGeneralError
    };
};